// Os AVISOS DO RESULTADO POR CONTRATO — o que cada um é e como se resolve
// (05/10/2026).
//
// O motor do resultado por contrato (dre-engine: fechamentoCC.js, maoObra.js,
// equipamento.js) grava em financeiro.divergencias tudo o que deixa dinheiro
// fora dos contratos em silêncio. Até 05/10/2026 esses avisos NÃO apareciam em
// tela nenhuma: a Deliberação tem uma aba por tipo, e nenhum destes tinha aba —
// 19 avisos abertos que ninguém via. Agora todos caem numa aba só.
//
// Os avisos somem sozinhos: quando a causa é corrigida, o próximo recálculo do
// mês aposenta o aviso. "Ignorar" é para o que é de propósito.
//
// Desde 06/10/2026: a mão de obra segue só as horas anotadas (o que não foi
// anotado fica parado e a pessoa é cobrada) e a análise começa em 2026 — a aba
// mostra só os avisos de 2026/01 em diante (2025 fica como está).

export const TIPOS_RESULTADO_CONTRATO: readonly string[] = [
  'fechamento_cc_nao_reconcilia',
  'projeto_horas_sem_contrato',
  'remuneracao_sem_pessoa',
  'mao_obra_sem_horas',
  'horas_sem_remuneracao',
  'clockify_user_duplicado',
  'checkin_sem_contrato',
  'checkin_contrato_inexistente',
  'checkin_aberto_ha_dias',
  'checkin_kit_sem_itens',
  'checkin_so_fim_de_semana',
  'checkin_bem_inexistente',
];

export const ABA_RESULTADO_CONTRATO = 'resultado_contrato';

export const ehAvisoDoResultado = (tipo?: string | null): boolean => TIPOS_RESULTADO_CONTRATO.includes(String(tipo || ''));

/** Primeiro mês analisado — o mesmo `corte_analise` do motor (fechamentoCC.js). */
export const CORTE_ANALISE = '2026/01';

/** O mês de um aviso sai do fim da chave (`tipo:quem:AAAA/MM`). */
export function competenciaDoAviso(chave?: string | null): string | null {
  const m = /:(\d{4}\/\d{2})$/.exec(String(chave || ''));
  return m ? m[1] : null;
}

/** Os avisos que a aba mostra: do resultado por contrato e de 2026 em diante. */
export function avisosDaAba<T extends { tipo?: string | null; chave_dedup?: string | null }>(itens: readonly T[]): T[] {
  return itens.filter(d => {
    if (!ehAvisoDoResultado(d.tipo)) return false;
    const comp = competenciaDoAviso(d.chave_dedup);
    return !comp || comp >= CORTE_ANALISE;
  });
}

export const ROTULO_DO_AVISO: Record<string, string> = {
  fechamento_cc_nao_reconcilia: 'Resultado por contrato não fecha com a DRE',
  projeto_horas_sem_contrato: 'Projeto com horas sem contrato',
  remuneracao_sem_pessoa: 'Remuneração sem a pessoa',
  mao_obra_sem_horas: 'Recebeu e não anotou horas',
  horas_sem_remuneracao: 'Anotou horas sem remuneração no mês',
  clockify_user_duplicado: 'Dois cadastros, um usuário do Clockify',
  checkin_sem_contrato: 'Kit em campo sem contrato',
  checkin_contrato_inexistente: 'Kit em campo com contrato que não existe',
  checkin_aberto_ha_dias: 'Kit em campo há muitos dias',
  checkin_kit_sem_itens: 'Kit em campo sem bens',
  checkin_so_fim_de_semana: 'Kit em campo só no fim de semana',
  checkin_bem_inexistente: 'Bem do kit fora do patrimônio',
};

/** Onde agir e o que acontece depois — uma frase por aviso. */
export const COMO_RESOLVER: Record<string, string> = {
  fechamento_cc_nao_reconcilia:
    'A soma dos contratos não bateu com a DRE geral neste mês. Refaça o mês em Financeiro › Resultado por contrato › Recalcular; se continuar, é defeito do cálculo — avise.',
  projeto_horas_sem_contrato:
    'As horas deste projeto não chegam a contrato nenhum. Ligue o projeto ao contrato em Administração › Integrações › Clockify (aba Projetos) — ou corrija o nome no Clockify para começar com o número do contrato (ex.: "P26037 - CLIENTE …"), que na sincronização da noite ele é ligado sozinho.',
  remuneracao_sem_pessoa:
    'Abra cada lançamento em Financeiro › Contas a Pagar e escolha a "Pessoa da equipe". Sem ela o valor fica parado: não segue as horas de ninguém. Se quem recebeu é um terceiro (não é da equipe), troque a categoria para Honorários Terceirizados.',
  mao_obra_sem_horas:
    'A pessoa recebeu no mês e não anotou nenhuma hora: o valor fica parado, fora dos contratos. Peça para ela anotar as horas do mês em Operacional › Horas (quem acompanha a equipe vê o que falta em Administrativo › Horas da equipe) — vale também para quem já saiu.',
  horas_sem_remuneracao:
    'Há horas anotadas, mas nenhuma remuneração do mês está com o nome desta pessoa: as horas não custaram nada aos contratos. Confira em Financeiro › Contas a Pagar se o pagamento dela está com a "Pessoa da equipe" e no mês certo.',
  clockify_user_duplicado:
    'Duas fichas apontam para o mesmo usuário do Clockify; vale a ficha ativa. Tire o usuário do Clockify da ficha que sobrou (Administrativo › RH).',
  checkin_sem_contrato: 'O kit foi para campo sem dizer o contrato: o custo do equipamento ficou na empresa. Corrija o check-in no app do Campo.',
  checkin_contrato_inexistente: 'O check-in aponta para um contrato que não existe mais. Corrija o check-in.',
  checkin_aberto_ha_dias: 'O kit está em campo sem devolução e continua cobrando o contrato. Faça o check-out.',
  checkin_kit_sem_itens: 'O kit não tinha bens quando saiu: nada foi cobrado. Confira o cadastro do kit.',
  checkin_so_fim_de_semana: 'O kit só esteve em campo em fim de semana ou feriado; a regra de dias úteis não cobra o contrato. Se deve cobrar, avise.',
  checkin_bem_inexistente: 'Um bem do kit não está no patrimônio. Confira o cadastro.',
};

const DEPOIS = 'O aviso some sozinho quando o mês for recalculado (toda madrugada, para os últimos meses).';

/** Por que estes avisos não têm "Marcar resolvida": quem resolve é a correção
 *  da causa. Marcado à mão, o aviso ficaria calado para sempre — mesmo com o
 *  problema ainda lá (ex.: 3.379 h de projeto sem contrato). */
export const SOBRE_IGNORAR = 'Não precisa dar baixa: corrigida a causa, o aviso some sozinho. '
  + 'Use "Ignorar" só para o que é de propósito — o aviso ignorado fica calado neste mês e só volta se aparecer um caso novo.';

/** As ações da tela para um aviso do resultado por contrato: só ignorar. */
export const ACOES_DO_AVISO: readonly { v: string; l: string }[] = [{ v: 'ignorar', l: 'Ignorar (é de propósito)' }];

/** Texto completo do "como resolver" de um aviso. */
export function comoResolver(tipo?: string | null): string {
  const t = COMO_RESOLVER[String(tipo || '')];
  return t ? `${t} ${DEPOIS}` : '';
}

